import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import ScreenshotPicker, { uploadScreenshots } from "./ScreenshotPicker";
import { Thumbnails } from "./TicketScreenshots";

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
}

/**
 * The conversation in a ticket (14.10.2026): the owner's questions, the
 * answers of who reported it, a word from a colleague — instead of a chat.
 * The other side is told by email; a message may carry screenshots.
 */
export default function TicketDiscussion({ ticket, me, screenshots = [], onSent }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const comments = ticket.comments || [];
  const waitingForMe = ticket.status === "needs_info" && me && me === ticket.reporter?.email;

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) { toast.error("Scrie mesajul."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/tickets/${ticket.id}/comments`, { text });
      if (files.length) await uploadScreenshots(ticket.id, files, { commentId: r.data.comment.id });
      toast.success(r.data.status !== ticket.status ? "Mesajul a fost trimis. Tichetul a revenit la echipă." : "Mesajul a fost trimis.");
      setText("");
      setFiles([]);
      onSent?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Mesajul nu a putut fi trimis."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section ref={box} className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="ticket-discussion">
      <h4 className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold mb-3">Discuție</h4>
      {waitingForMe && (
        <p className="mb-3 text-sm text-[#8a5a00] bg-[#F5B841]/15 rounded-lg px-3 py-2">
          Echipa are nevoie de câteva detalii. Răspunde aici și tichetul revine la ea.
        </p>
      )}
      {comments.length === 0 ? (
        <p className="text-sm text-[#6B7280] mb-4">Încă nu a scris nimeni în tichet.</p>
      ) : (
        <ol className="space-y-3 mb-4" data-testid="ticket-comments">
          {comments.map((c) => {
            const mine = me && c.author?.email === me;
            const shots = screenshots.filter((s) => s.comment_id === c.id);
            return (
              <li key={c.id} className={`rounded-xl px-4 py-3 ${mine ? "bg-[#965EA7]/[0.07] ml-6" : "bg-[#F5F6F6] mr-6"}`}>
                <div className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="font-semibold text-[#373A45]">{mine ? "Tu" : c.author?.name || c.author?.email}</span>
                  <span className="text-[#6B7280] tabular-nums whitespace-nowrap">{when(c.created_at)}</span>
                </div>
                <p className="[overflow-wrap:anywhere] mt-1 text-sm text-[#373A45] whitespace-pre-wrap leading-relaxed">{c.text}</p>
                {shots.length > 0 && <Thumbnails items={shots} small className="mt-2" />}
              </li>
            );
          })}
        </ol>
      )}

      <form onSubmit={send} className="space-y-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={5000}
          placeholder={waitingForMe ? "Scrie detaliile cerute…" : "Scrie un mesaj în tichet…"}
          className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#965EA7] outline-none"
          data-testid="ticket-comment-text" />
        <ScreenshotPicker files={files} onChange={setFiles} max={3} pasteTarget={box} />
        <div className="flex justify-end">
          <button type="submit" disabled={busy}
            className="rounded-full px-4 py-2 text-xs font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] disabled:opacity-60 inline-flex items-center gap-1.5"
            data-testid="ticket-comment-send">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Trimite mesajul
          </button>
        </div>
      </form>
    </section>
  );
}
